/**
 * Typeahead/autocomplete — search-as-you-type with dropdown results.
 *
 * Shared component (SeeMe bundle) used by Loop and Houses.
 *
 * Two modes:
 *   1. Form mode — populates hidden inputs, fires typeahead:select event.
 *      The parent form handles submission (via HTMX or regular submit).
 *   2. Save mode — POSTs directly on selection, no form needed.
 *      Activated by data-typeahead-save on the container.
 *
 * Markup (form mode, standalone):
 *   <div class="typeahead" data-typeahead-url="/search/" data-typeahead-target="field_id">
 *       <input type="text" class="form-control typeahead-input" placeholder="Search..." autocomplete="off">
 *       <input type="hidden" name="field_id" value="">
 *       <div class="typeahead-results"></div>
 *   </div>
 *
 *   A floating results list (any .typeahead that is not itself a .dropdown) is
 *   fitted to the screen each time it renders, and again on scroll, resize and
 *   phone-keyboard changes: it opens below the input when it fits, above when
 *   only that fits (.typeahead--up on the container), and otherwise on the
 *   roomier side with .typeahead-results capped so the rows scroll and a
 *   .typeahead-footer stays visible. The CSS 240px max-height is the ceiling.
 *   Room is also bounded by clipping ancestors, and by a scroll container
 *   (a sheet body): above by its origin, since nothing can scroll to a panel
 *   that opened into negative overflow, and below by its visible bottom edge,
 *   so a sheet footer doesn't cover the list.  Inside a menu open as a phone sheet
 *   (.dropdown-sheet.show and window._cc.isSheet) the list is in flow and is
 *   not fitted.
 *
 * Markup (form mode, standalone with footer):
 *   <div class="typeahead" data-typeahead-url="/search/" data-typeahead-target="field_id">
 *       <input type="text" class="form-control typeahead-input" placeholder="Search..." autocomplete="off">
 *       <div class="typeahead-panel">
 *           <div class="typeahead-results"></div>
 *           <hr class="dropdown-divider">
 *           <a class="dropdown-item typeahead-footer" href="#">
 *               <i class="fa-solid fa-plus"></i> Add New
 *           </a>
 *       </div>
 *       <input type="hidden" name="field_id" value="">
 *   </div>
 *
 * Markup (form mode, with clear button):
 *   <div class="typeahead" data-typeahead-url="/search/" data-typeahead-target="field_id">
 *       <div class="input-group">
 *           <input type="text" class="form-control typeahead-input" placeholder="Search..." autocomplete="off">
 *           <button type="button" class="btn-ghost typeahead-clear-btn" style="display:none" aria-label="Clear"><i class="fa-solid fa-xmark"></i></button>
 *       </div>
 *       <div class="typeahead-results"></div>
 *       <input type="hidden" name="field_id" value="">
 *   </div>
 *   The button shows after a pick and hides on clear.  Clicking it empties the
 *   input and all three hidden targets, then fires typeahead:select with an
 *   empty id.  Render it visible (no display:none) when the field has a value.
 *
 * Markup (save mode, inside dropdown):
 *   <div class="dropdown typeahead"
 *        data-typeahead-url="/search/"
 *        data-typeahead-save="/save/"
 *        data-typeahead-field="contact_id"
 *        data-typeahead-new-field="contact_new">
 *       <span class="data-table__editable" data-dropdown>Value</span>
 *       <div class="dropdown-menu dropdown-form" data-dropdown-keep-open>
 *           <label class="dropdown-form__label">Label</label>
 *           <input type="text" class="form-control form-control-sm typeahead-input" placeholder="Search...">
 *           <div class="typeahead-results"></div>
 *       </div>
 *   </div>
 *   When the menu opens (dropdown:open), its input and results are cleared and
 *   the input is focused.  On phones dropdowns.js portals the menu to body as
 *   a sheet; ownerOf / findIn follow its _dropdownOwner / _portaledMenu links,
 *   and a successful save closes it through window._cc.closeDropdown(menu).
 *
 * Data attributes:
 *   data-typeahead-url         — GET endpoint, receives ?q=..., returns [{id, title, project?, detail?, value?}, ...];
 *                                project and detail render as stacked muted lines under the
 *                                title: project wraps, detail truncates with an ellipsis.
 *                                value is a fill value for listeners, sent as detail.value
 *                                (the input and name-target still get the title)
 *   data-typeahead-target      — name of hidden input to populate with selected ID (form mode)
 *   data-typeahead-new-target  — name of hidden input for new name when "Add new" is clicked (form mode)
 *   data-typeahead-name-target — name of hidden input to store the display name (form mode)
 *   data-typeahead-save        — POST endpoint for auto-save on selection (save mode)
 *   data-typeahead-field       — POST param name for the selected ID (save mode)
 *   data-typeahead-new-field   — POST param name for new name (save mode, "Add new")
 *   data-typeahead-exclude     — ID to exclude from results (appended as &exclude=)
 *   data-typeahead-append-target  — CSS selector; append response HTML here after save
 *   data-typeahead-append-addclass — class to add to append target
 *   data-typeahead-swap-target    — CSS selector; replace this element with response HTML after save
 *   data-typeahead-navigate       — filter param name; calls applyDocFilter() instead of selecting
 *
 * Events (dispatched on .typeahead container, bubbles):
 *   typeahead:select — detail: { id, name, value }
 *     pick:    id set, name set, value is the result's value or else the name
 *     Add new: id '', name and value are the typed text
 *     clear:   id '', name '', value ''
 */

(function () {
    'use strict';

    // ── Helpers (graceful fallbacks for host-app utilities) ──────────────

    function getCsrfToken() {
        if (window._cc && window._cc.getCsrfToken) return window._cc.getCsrfToken();
        var el = document.querySelector('[name=csrfmiddlewaretoken]');
        return el ? el.value : '';
    }

    function setSafeHTML(el, html) {
        if (window._cc && window._cc.safeSetHTML) {
            window._cc.safeSetHTML(el, html);
        } else {
            el.innerHTML = html;
        }
    }

    // dropdowns.js portals a sheet-mode menu to body and links both ways:
    // menu._dropdownOwner is the .dropdown wrapper, wrapper._portaledMenu the
    // menu.  ownerOf looks up and findIn looks down across that gap (defined in
    // dropdowns.js, shared through window._cc, which loads first).
    var ownerOf = window._cc.ownerOf;
    var findIn = window._cc.findIn;
    // True only while the menu is open as a phone sheet; the .dropdown-sheet
    // class alone also sits on closed and hand-authored menus at any width.
    var isSheet = window._cc.isSheet;

    // A hidden target lives in the enclosing form (found through the portal link
    // when the typeahead sits in a portaled menu), or in the wrapper or its menu.
    function targetInput(container, name) {
        var sel = 'input[name="' + name + '"]';
        var form = ownerOf(container, 'form');
        return (form && form.querySelector(sel)) || findIn(container, sel);
    }

    function refreshSheet() {
        if (window._cc && window._cc.refreshCurrentSheet) window._cc.refreshCurrentSheet();
    }

    function escapeHtml(str) {
        var div = document.createElement('div');
        div.textContent = str;
        // Result labels also appear inside double-quoted data attributes.
        return div.innerHTML.replace(/"/g, '&quot;');
    }

    var debounceTimer;
    var SPINNER_HTML = '<div style="padding:12px;text-align:center"><div class="spinner spinner--sm spinner--muted"></div></div>';

    // ── Mode detection ──────────────────────────────────────────────────

    function isSaveMode(container) {
        return container.hasAttribute('data-typeahead-save');
    }

    // ── Save mode: POST on selection ────────────────────────────────────

    // Views answer a rejected save with a short plain-text reason ("Document not
    // found"). Anything long or markup-shaped is a debug/error page, not a message
    // for the user — fall back to the generic text.
    function saveFailed(responseText) {
        var msg = (responseText || '').trim();
        if (!msg || msg.length > 120 || msg.indexOf('<') !== -1) msg = 'Could not save';
        if (window.showToast) window.showToast(msg, 'error');
    }

    function typeaheadPost(container, value, newName) {
        var saveUrl = container.getAttribute('data-typeahead-save');
        var field = container.getAttribute('data-typeahead-field');
        var newField = container.getAttribute('data-typeahead-new-field') || '';
        var body = field + '=' + encodeURIComponent(value);
        if (newName && newField) {
            body += '&' + newField + '=' + encodeURIComponent(newName);
        }
        var xhr = new XMLHttpRequest();
        xhr.open('POST', saveUrl);
        xhr.setRequestHeader('Content-Type', 'application/x-www-form-urlencoded');
        xhr.setRequestHeader('X-CSRFToken', getCsrfToken());
        xhr.onload = function () {
            if (xhr.status >= 200 && xhr.status < 300) {
                // closeDropdown restores a portaled sheet and hides its backdrop.
                var menu = findIn(container, '.dropdown-menu');
                if (menu && window._cc && window._cc.closeDropdown) {
                    window._cc.closeDropdown(menu);
                } else if (menu) {
                    menu.classList.remove('show');
                }
                var appendSel = container.getAttribute('data-typeahead-append-target');
                var swapSel = container.getAttribute('data-typeahead-swap-target');
                if (appendSel) {
                    var el = document.querySelector(appendSel);
                    if (el && xhr.responseText.trim()) {
                        var addCls = container.getAttribute('data-typeahead-append-addclass');
                        if (addCls) el.classList.add(addCls);
                        var tmp = document.createElement('div');
                        tmp.innerHTML = xhr.responseText;
                        while (tmp.firstChild) {
                            var node = tmp.firstChild;
                            el.appendChild(node);
                            if (window.htmx) htmx.process(node);
                        }
                    }
                } else if (swapSel) {
                    var el = document.querySelector(swapSel);
                    if (el) {
                        el.outerHTML = xhr.responseText;
                        var fresh = document.querySelector(swapSel);
                        if (fresh && window.htmx) htmx.process(fresh);
                    }
                } else {
                    refreshSheet();
                }
            } else {
                saveFailed(xhr.responseText);
            }
        };
        xhr.onerror = function () { saveFailed(''); };
        xhr.send(body);
    }

    // ── Form mode: populate hidden inputs ───────────────────────────────

    // id and name both '' is a clear: empties the input and every target.
    function typeaheadFormSelect(container, id, name) {
        var clearBtn = findIn(container, '.typeahead-clear-btn');
        if (clearBtn) clearBtn.style.display = id ? '' : 'none';
        var navParam = container.getAttribute('data-typeahead-navigate');
        if (navParam) {
            if (window.applyDocFilter) window.applyDocFilter(navParam, id || '');
            return;
        }
        var targetName = container.getAttribute('data-typeahead-target');
        var newTargetName = container.getAttribute('data-typeahead-new-target');
        var nameTargetName = container.getAttribute('data-typeahead-name-target');
        if (targetName) {
            var hidden = targetInput(container, targetName);
            if (hidden) hidden.value = id || '';
        }
        if (newTargetName) {
            var newHidden = targetInput(container, newTargetName);
            if (newHidden) newHidden.value = id ? '' : name;
        }
        if (nameTargetName) {
            var nameInput = targetInput(container, nameTargetName);
            if (nameInput) nameInput.value = name;
        }
        var input = findIn(container, '.typeahead-input');
        if (input && (name || !id)) input.value = name;
        var resultsEl = findIn(container, '.typeahead-results');
        if (resultsEl) {
            resultsEl.innerHTML = '';
            resultsEl.classList.remove('show');
        }
    }

    // ── Constrain results width + height ────────────────────────────────

    // The list's own CSS gap under the input, and the screen-edge margin
    // dropdowns.js uses, in px.
    var GAP = 4;
    var EDGE = 8;
    var MIN_LIST = 48;

    // Visible band of the screen, in layout-viewport coordinates (what
    // getBoundingClientRect measures in). visualViewport shrinks for the phone
    // keyboard; window.innerHeight does not. --dropdown-bottom-offset is the
    // same sticky-footer allowance dropdowns.js honours.
    function visibleBand(container) {
        var vv = window.visualViewport;
        var top = vv ? vv.offsetTop : 0;
        var bottom = vv ? vv.offsetTop + vv.height : window.innerHeight;
        bottom -= parseInt(getComputedStyle(container).getPropertyValue('--dropdown-bottom-offset')) || 0;
        return { top: top + EDGE, bottom: bottom - EDGE };
    }

    // What the container's ancestors make unreachable or hidden, in the same
    // coordinates. A clipping ancestor (overflow hidden/clip) bounds both sides.
    // A scroll container (auto/scroll) bounds the top at its scroll origin:
    // content above it is negative overflow that no scroll reaches. It bounds
    // the bottom at its visible bottom edge: content below is reachable only by
    // scrolling the container, and in a sheet it sits under the footer. Walks
    // up to and including a position:fixed ancestor, as dropdowns.js
    // getClipRect does — overflow above that point no longer clips. The sheet
    // body is the real case for both: a panel opened upward from an input near
    // the top of the body sat above the body's origin, and one opened downward
    // ran under the sheet footer with only a row or two showing.
    function ancestorBounds(container) {
        var top = -Infinity, bottom = Infinity;
        var node = container.parentElement;
        while (node && node !== document.body && node !== document.documentElement) {
            var cs = getComputedStyle(node);
            var ov = cs.overflowY;
            if (ov === 'hidden' || ov === 'clip') {
                var rect = node.getBoundingClientRect();
                top = Math.max(top, rect.top);
                bottom = Math.min(bottom, rect.bottom);
            } else if (ov === 'auto' || ov === 'scroll') {
                var box = node.getBoundingClientRect();
                top = Math.max(top, box.top + node.clientTop - node.scrollTop);
                bottom = Math.min(bottom, box.top + node.clientTop + node.clientHeight);
            }
            if (cs.position === 'fixed') break;
            node = node.parentElement;
        }
        return { top: top, bottom: bottom };
    }

    // A floating results panel is position:absolute under the input, so nothing
    // limits it to the screen — on a phone the list ran past the fold and cut
    // off the "Add new" footer, and no scroll could reach it. Same rule as
    // fitPortalHeight in dropdowns.js: open below when it fits, else above when
    // it fits, else on the roomier side with the list capped so the rows scroll
    // and the footer stays visible under them.
    //
    // Only for the floating case. Inside a .dropdown.typeahead the list is in
    // normal flow in the menu and the menu's own placement owns the height.
    //
    // Measures scrollHeight rather than clearing the cap first, so a re-run on
    // scroll/resize doesn't reset the list's own scroll position.
    function fitResultsHeight(container, constrainEl, resultsEl) {
        if (container.classList.contains('dropdown')) return;
        // The CSS cap (240px), read once before any inline cap hides it.
        if (!('_cssMaxHeight' in resultsEl)) {
            resultsEl._cssMaxHeight = parseFloat(getComputedStyle(resultsEl).maxHeight);
        }
        var list = resultsEl.scrollHeight + resultsEl.offsetHeight - resultsEl.clientHeight;
        if (!isNaN(resultsEl._cssMaxHeight)) list = Math.min(list, resultsEl._cssMaxHeight);
        // Footer, divider and panel padding — whatever the panel adds around the list.
        var extra = constrainEl.offsetHeight - resultsEl.offsetHeight;
        var need = list + extra;

        var band = visibleBand(container);
        var bounds = ancestorBounds(container);
        var anchor = container.getBoundingClientRect();
        var below = Math.min(band.bottom, bounds.bottom) - anchor.bottom - GAP;
        var above = anchor.top - GAP - Math.max(band.top, bounds.top);

        var up, cap;
        if (need <= below) {
            up = false; cap = '';
        } else if (need <= above) {
            up = true; cap = '';
        } else {
            up = above > below;
            cap = Math.max(Math.max(above, below) - extra, MIN_LIST) + 'px';
        }
        resultsEl.style.maxHeight = cap;
        container.classList.toggle('typeahead--up', up);
    }

    function constrainResults(container) {
        var resultsEl = findIn(container, '.typeahead-results');
        if (!resultsEl) return;
        // When inside a panel, constrain the panel (it owns positioning)
        var panel = resultsEl.closest('.typeahead-panel');
        var constrainEl = panel || resultsEl;
        // An open phone sheet puts the list in flow.  Drop any fit left from
        // another width, since inline styles would beat the sheet CSS.
        var sheetMenu = resultsEl.closest('.dropdown-sheet.show');
        if (sheetMenu && isSheet(sheetMenu)) {
            constrainEl.style.width = constrainEl.style.left = constrainEl.style.right = '';
            resultsEl.style.maxHeight = '';
            container.classList.remove('typeahead--up');
            return;
        }
        // Match the search input's width so results fill the dropdown at whatever
        // width it is (e.g. .dropdown-menu--widest) instead of a fixed legacy 250px
        // that truncated titles early in wider menus.
        var input = findIn(container, '.typeahead-input');
        constrainEl.style.width = (input && input.offsetWidth ? input.offsetWidth : 250) + 'px';
        // Rows truncate with an ellipsis.  A stacked row truncates each of its
        // lines instead (a flex column won't ellipsize its children), except
        // the .dropdown-item__meta line, which wraps.
        resultsEl.querySelectorAll('.dropdown-item').forEach(function (el) {
            el.style.overflow = 'hidden';
            var lines = el.classList.contains('dropdown-item--stacked')
                ? el.querySelectorAll(':scope > :not(.dropdown-item__meta)')
                : [el];
            Array.prototype.forEach.call(lines, function (line) {
                line.style.maxWidth = '100%';
                line.style.overflow = 'hidden';
                line.style.textOverflow = 'ellipsis';
                line.style.whiteSpace = 'nowrap';
            });
        });
        var rect = constrainEl.getBoundingClientRect();
        if (rect.right > window.innerWidth) {
            constrainEl.style.right = '0';
            constrainEl.style.left = 'auto';
        }
        fitResultsHeight(container, constrainEl, resultsEl);
    }

    // Re-fit every open floating list when the screen or the anchor moves —
    // page scroll, window resize, and the phone keyboard opening or closing
    // (visualViewport resize). Coalesced to one pass per frame, like the
    // dropdowns.js observer. A scroll inside the list itself is the rows
    // scrolling; nothing has moved, so skip it.  A list in an open phone
    // sheet is in flow and is skipped too.
    var refitPending = false;
    function refitOpenResults(e) {
        if (e && e.type === 'scroll' && e.target instanceof Element &&
            e.target.closest('.typeahead-results, .typeahead-panel')) return;
        if (refitPending || !document.querySelector('.typeahead-results.show')) return;
        refitPending = true;
        requestAnimationFrame(function () {
            refitPending = false;
            document.querySelectorAll('.typeahead-results.show').forEach(function (resultsEl) {
                var sheetMenu = resultsEl.closest('.dropdown-sheet.show');
                if (sheetMenu && isSheet(sheetMenu)) return;
                var container = ownerOf(resultsEl, '.typeahead');
                if (!container || container.hasAttribute('data-combobox') || container.classList.contains('dropdown')) return;
                constrainResults(container);
            });
        });
    }
    window.addEventListener('resize', refitOpenResults);
    window.addEventListener('scroll', refitOpenResults, true);
    if (window.visualViewport) window.visualViewport.addEventListener('resize', refitOpenResults);

    // ── Search on input ─────────────────────────────────────────────────

    document.addEventListener('input', function (e) {
        if (!e.target || !e.target.closest) return;
        var input = e.target.closest('.typeahead-input');
        if (!input) return;
        var container = ownerOf(input, '.typeahead');
        if (!container || container.hasAttribute('data-combobox')) return;
        var resultsEl = findIn(container, '.typeahead-results');
        if (!resultsEl) return;
        var q = input.value.trim();
        clearTimeout(debounceTimer);

        if (q.length < 2) {
            if (q.length === 1) {
                resultsEl.innerHTML = SPINNER_HTML;
                resultsEl.classList.add('show');
                constrainResults(container);
            } else {
                resultsEl.innerHTML = '';
                resultsEl.classList.remove('show');
            }
            // Clear hidden inputs when query is too short (form mode only).
            // Not typeaheadFormSelect: that would wipe the typed character.
            if (!isSaveMode(container)) {
                ['data-typeahead-target', 'data-typeahead-new-target', 'data-typeahead-name-target'].forEach(function (attr) {
                    var targetName = container.getAttribute(attr);
                    var h = targetName && targetInput(container, targetName);
                    if (h) h.value = '';
                });
                var shortClearBtn = findIn(container, '.typeahead-clear-btn');
                if (shortClearBtn) shortClearBtn.style.display = 'none';
            }
            return;
        }

        debounceTimer = setTimeout(function () {
            var url = container.getAttribute('data-typeahead-url');
            var exclude = container.getAttribute('data-typeahead-exclude') || '';
            resultsEl.innerHTML = SPINNER_HTML;
            resultsEl.classList.add('show');
            // Place the spinner too, so a cap or .typeahead--up left by the
            // last render doesn't show for the 300ms before results land.
            constrainResults(container);
            var xhr = new XMLHttpRequest();
            var sep = url.indexOf('?') === -1 ? '?' : '&';
            xhr.open('GET', url + sep + 'q=' + encodeURIComponent(q) + (exclude ? '&exclude=' + exclude : ''));
            xhr.onload = function () {
                if (xhr.status !== 200) return;
                var items = JSON.parse(xhr.responseText);
                var keepOpen = container.closest('[data-dropdown-keep-open]') ? ' data-dropdown-keep-open' : '';
                var html = items.map(function (item) {
                    // Extra lines render stacked under the title: project as a
                    // wrapping .dropdown-item__meta line, detail as a muted line
                    // that constrainResults truncates.
                    var stacked = item.project || item.detail;
                    return '<a class="dropdown-item' + (stacked ? ' dropdown-item--stacked' : '') + '" href="#"' + keepOpen +
                        ' data-typeahead-id="' + item.id +
                        '" data-typeahead-name="' + escapeHtml(item.title) + '"' +
                        (item.value ? ' data-typeahead-value="' + escapeHtml(item.value) + '"' : '') + '>' +
                        (stacked
                            ? '<span>' + escapeHtml(item.title) + '</span>' +
                              (item.project ? '<span class="dropdown-item__meta small text-muted">' + escapeHtml(item.project) + '</span>' : '') +
                              (item.detail ? '<span class="small text-muted">' + escapeHtml(item.detail) + '</span>' : '')
                            : escapeHtml(item.title)) +
                        '</a>';
                }).join('');
                // "Add new" link when new-field/new-target is configured
                if (container.hasAttribute('data-typeahead-new-field') || container.hasAttribute('data-typeahead-new-target')) {
                    if (html) html += '<hr class="dropdown-divider">';
                    html += '<a class="dropdown-item" href="#" data-typeahead-add-new><i class="fa-solid fa-plus"></i> Add "' + escapeHtml(q) + '"</a>';
                }
                if (!html) {
                    html = '<div class="dropdown-item text-muted">No results</div>';
                }
                setSafeHTML(resultsEl, html);
                resultsEl.classList.add('show');
                constrainResults(container);
            };
            xhr.send();
        }, 300);
    });

    // ── Click: select result, add new, or clear ─────────────────────────

    document.addEventListener('click', function (e) {
        if (!e.target || !e.target.closest) return;

        // Footer link inside panel — close results, let the link do its thing
        var footer = e.target.closest('.typeahead-footer');
        if (footer) {
            var container = ownerOf(footer, '.typeahead');
            if (container) {
                var resultsEl = findIn(container, '.typeahead-results');
                if (resultsEl) {
                    resultsEl.innerHTML = '';
                    resultsEl.classList.remove('show');
                }
            }
            return;
        }

        // "Add new" action
        var addNew = e.target.closest('[data-typeahead-add-new]');
        if (addNew) {
            e.preventDefault();
            var container = ownerOf(addNew, '.typeahead');
            if (!container) return;
            var input = findIn(container, '.typeahead-input');
            var name = input ? input.value.trim() : '';
            if (isSaveMode(container)) {
                typeaheadPost(container, '', name);
            } else {
                typeaheadFormSelect(container, '', name);
            }
            container.dispatchEvent(new CustomEvent('typeahead:select', {
                bubbles: true,
                detail: { id: '', name: name, value: name }
            }));
            var resultsEl = findIn(container, '.typeahead-results');
            if (resultsEl) {
                resultsEl.innerHTML = '';
                resultsEl.classList.remove('show');
            }
            return;
        }

        // Select a result
        var resultItem = e.target.closest('[data-typeahead-id]');
        if (resultItem) {
            e.preventDefault();
            var container = ownerOf(resultItem, '.typeahead');
            if (!container) return;
            var id = resultItem.getAttribute('data-typeahead-id');
            var name = resultItem.getAttribute('data-typeahead-name') || resultItem.textContent.trim();
            var value = resultItem.getAttribute('data-typeahead-value') || name;
            if (isSaveMode(container)) {
                typeaheadPost(container, id);
            } else {
                typeaheadFormSelect(container, id, name);
            }
            container.dispatchEvent(new CustomEvent('typeahead:select', {
                bubbles: true,
                detail: { id: id, name: name, value: value }
            }));
            return;
        }

        // Clear button (save-mode style: .typeahead-clear)
        var clearVal = e.target.closest('.typeahead-clear');
        if (clearVal) {
            e.preventDefault();
            var container = ownerOf(clearVal, '.typeahead');
            if (!container) return;
            if (isSaveMode(container)) {
                typeaheadPost(container, '');
            } else {
                typeaheadFormSelect(container, '', '');
            }
            container.dispatchEvent(new CustomEvent('typeahead:select', {
                bubbles: true,
                detail: { id: '', name: '', value: '' }
            }));
            return;
        }

        // Clear button (form-mode style: .typeahead-clear-btn)
        var clearBtn = e.target.closest('.typeahead-clear-btn');
        if (clearBtn) {
            e.preventDefault();
            var container = ownerOf(clearBtn, '.typeahead');
            if (!container) return;
            typeaheadFormSelect(container, '', '');
            var input = findIn(container, '.typeahead-input');
            if (input) input.focus();
            container.dispatchEvent(new CustomEvent('typeahead:select', {
                bubbles: true,
                detail: { id: '', name: '', value: '' }
            }));
            return;
        }
    });

    // ── Clear and focus the input when a .dropdown.typeahead menu opens ──

    // dropdown:open fires inside the user's click, after the menu is placed
    // (and portaled, on phones).  Focus runs synchronously so iOS raises the
    // keyboard.
    document.addEventListener('dropdown:open', function (e) {
        var menu = e.detail && e.detail.menu;
        if (!menu) return;
        var container = ownerOf(menu, '.dropdown');
        if (!container || !container.classList.contains('typeahead') || container.hasAttribute('data-combobox')) return;
        var input = menu.querySelector('.typeahead-input');
        if (!input) return;
        input.value = '';
        var resultsEl = menu.querySelector('.typeahead-results');
        if (resultsEl) { resultsEl.innerHTML = ''; resultsEl.classList.remove('show'); }
        input.focus({ preventScroll: true });
    });

    // ── Recent items on focus (Loop: recent tasks) ──────────────────────

    document.addEventListener('focusin', function (e) {
        if (!e.target || !e.target.closest) return;
        var input = e.target.closest('.typeahead-input');
        if (!input) return;
        var container = ownerOf(input, '.typeahead');
        if (!container || container.hasAttribute('data-combobox')) return;
        if (input.value.trim()) return;
        var url = container.getAttribute('data-typeahead-url') || '';
        if (url.indexOf('tasks/search-api') === -1) return;
        if (!window._cc || !window._cc.getRecentTasks) return;
        var exclude = container.getAttribute('data-typeahead-exclude') || '';
        var recent = window._cc.getRecentTasks(5).filter(function (item) {
            return String(item.id) !== exclude;
        });
        if (!recent.length) return;
        var resultsEl = findIn(container, '.typeahead-results');
        if (!resultsEl) return;
        var keepOpen = (container.closest('[data-dropdown-keep-open]') || findIn(container, '[data-dropdown-keep-open]')) ? ' data-dropdown-keep-open' : '';
        var html = '<div class="dropdown-header">Recent</div>';
        html += recent.map(function (item) {
            return '<a class="dropdown-item" href="#"' + keepOpen + ' data-typeahead-id="' + item.id + '" data-typeahead-name="' + escapeHtml(item.title) + '">' + escapeHtml(item.title) + '</a>';
        }).join('');
        html += '<div class="dropdown-header" style="font-weight:normal;opacity:0.5;">Type to search all tasks\u2026</div>';
        setSafeHTML(resultsEl, html);
        resultsEl.classList.add('show');
        constrainResults(container);
    });

    // ── Click outside: close open results ───────────────────────────────

    document.addEventListener('click', function (e) {
        if (!e.target || !e.target.closest) return;
        if (!ownerOf(e.target, '.typeahead')) {
            document.querySelectorAll('.typeahead-results.show').forEach(function (el) {
                if (ownerOf(el, '[data-combobox]')) return;
                el.classList.remove('show');
            });
        }
    });

})();
